import React, { useEffect } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Link } from "react-router-dom";
import { getSchoolUrl } from "@/lib/slug";
import { StarRating } from "@/components/site/Stars";
import { formatIDR } from "@/lib/compareStore";
import { useFlags } from "@/lib/flags";

// Fix default marker icons in Webpack/CRA
const defaultIcon = new L.Icon({
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

// Custom colored pin factory (SVG) so we can tint by school type.
const colorPin = (color) =>
  L.divIcon({
    className: "tsr-pin",
    html: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 32" width="30" height="40">
      <path d="M12 0C5.4 0 0 5.4 0 12c0 8 12 20 12 20s12-12 12-20C24 5.4 18.6 0 12 0z" fill="${color}"/>
      <circle cx="12" cy="12" r="5" fill="#fff"/>
    </svg>`,
    iconSize: [30, 40],
    iconAnchor: [15, 40],
    popupAnchor: [0, -34],
  });

const TYPE_COLOR = {
  negeri: "#1E3A8A",
  swasta: "#E07A5F",
  internasional: "#7C3AED",
  mi: "#059669",
  mts: "#059669",
  ma: "#059669",
  pesantren: "#059669",
};

// Recalculate map bounds to fit all markers
function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng]));
    map.fitBounds(bounds, { padding: [40, 40] });
  }, [map, points]);
  return null;
}

export default function SchoolMap({ schools }) {
  const { school_reviews_enabled: reviewsOn } = useFlags();
  const points = schools
    .filter((s) => typeof s.latitude === "number" && typeof s.longitude === "number")
    .map((s) => ({ ...s, lat: s.latitude, lng: s.longitude }));

  // Fallback center: Jakarta
  const center = points[0] ? [points[0].lat, points[0].lng] : [-6.2, 106.85];

  return (
    <div data-testid="school-map" className="rounded-2xl overflow-hidden border border-[#EAE7E0] shadow-[0_2px_10px_rgba(0,0,0,0.04)] h-[560px]">
      <MapContainer center={center} zoom={11} scrollWheelZoom style={{ height: "100%", width: "100%" }}>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FitBounds points={points} />
        {points.map((s) => (
          <Marker
            key={s.school_id}
            position={[s.lat, s.lng]}
            icon={colorPin(TYPE_COLOR[s.school_type] || "#1E3A8A")}
          >
            <Popup>
              <div style={{ minWidth: 220 }}>
                <div className="text-xs uppercase tracking-widest text-[#4B5563]">
                  {s.city} · {s.school_type}
                </div>
                <div className="font-serif-display text-base text-[#1F2937] mt-1 leading-tight">{s.name}</div>
{reviewsOn && <div className="mt-2 flex items-center gap-2">
                  <StarRating value={s.avg_rating || 0} size={12} />
                  <span className="text-xs text-[#4B5563]">{s.review_count} review</span>
                </div>}
                <div className="mt-1 text-xs text-[#1E3A8A] font-medium">{formatIDR(s.tuition_monthly)}</div>
                <Link
                  to={getSchoolUrl(s)}
                  data-testid={`map-popup-link-${s.school_id}`}
                  className="mt-2 inline-block text-xs font-medium text-[#E07A5F] hover:underline"
                >
                  Lihat detail →
                </Link>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
}
